<mat-card>
  <mat-card-content>
    <h2 class="main-title">MIPS Eligibility Check</h2>

    <div class="criteria-section" [formGroup]="eligibilityForm">
      <h3 class="section-subtitle">Select Submission Criteria</h3>

      <div class="header-controls">
        <mat-form-field appearance="outline">
          <mat-label>Performance Year</mat-label>
          <mat-select formControlName="performanceYear">
            @for (year of availableYears; track $index) {
              <mat-option [value]="year">{{ year }}</mat-option>
            }
          </mat-select>
          @if (isFieldInvalid("performanceYear")) {
            <mat-error>Please select a performance year</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Practice</mat-label>
          <mat-select formControlName="practice">
            <mat-option disabled>
              <ngx-mat-select-search
                [formControl]="orgSearchControl"
                placeholderLabel="Search organizations..."
                noEntriesFoundLabel="No organizations found"
              >
              </ngx-mat-select-search>
            </mat-option>
            @for (org of filteredOrgs$ | async; track org.id) {
              <mat-option [value]="org.id">{{ org.name }}</mat-option>
            }
          </mat-select>
          @if (isFieldInvalid("practice")) {
            <mat-error>Please select a practice</mat-error>
          }
        </mat-form-field>

        @if (eligibilityForm.get("practice")?.value && !hasNoTins) {
          @if (loadingTins()) {
            <mat-form-field appearance="outline">
              <mat-label>Loading TINs...</mat-label>
              <input matInput disabled />
            </mat-form-field>
          } @else if (hasMultipleTins) {
            <mat-form-field appearance="outline">
              <mat-label>Select TIN</mat-label>
              <mat-select formControlName="tin">
                @for (tin of practiceTins; track tin.tin) {
                  <mat-option [value]="tin.tin">{{ tin.tin }}</mat-option>
                }
              </mat-select>
              @if (isFieldInvalid("tin")) {
                <mat-error>Please select a TIN</mat-error>
              }
            </mat-form-field>
          } @else if (hasSingleTin) {
            <mat-form-field appearance="outline">
              <mat-label>TIN</mat-label>
              <input matInput [value]="practiceTins[0].tin" disabled />
            </mat-form-field>
          }
        }
      </div>

      @if (eligibilityForm.get("practice")?.value && hasNoTins) {
        <div class="no-tins-warning">
          <mat-icon>warning</mat-icon>
          <span>No TINs configured for this organization. Please add TINs in the Organization settings before starting a MIPS submission.</span>
        </div>
      }

      @if (orgSubmissions() && orgSubmissions()!.length > 0) {
        <div class="existing-submissions-section">
          <h3 class="section-subtitle">Existing Submissions</h3>
          <div class="view-submissions-row">
            <button mat-stroked-button color="primary" (click)="openSubmissionsDialog()">
              <mat-icon>list_alt</mat-icon>
              View Submissions ({{ orgSubmissions()!.length }})
            </button>
          </div>
        </div>
      } @else if (orgSubmissionsLoading()) {
        <div class="view-submissions-row">
          <mat-spinner diameter="20" style="display:inline-block; vertical-align:middle; margin-right:8px;"></mat-spinner>
          <span style="vertical-align:middle; color:#757575; font-size:14px;">Loading submissions...</span>
        </div>
      }
    </div>

    @if (!hasNoTins) {
    <div class="npi-check-section">
      <h3 class="section-title">Check Clinician Eligibility</h3>

      <form [formGroup]="eligibilityForm" (ngSubmit)="checkEligibility()">
        <div class="npi-input-row">
          <mat-form-field appearance="outline" class="npi-field">
            <mat-label>Select Clinician</mat-label>
            <mat-select formControlName="npi" panelClass="searchable-panel">
              <mat-option disabled>
                <ngx-mat-select-search
                  [formControl]="clinicianSearchControl"
                  placeholderLabel="Search Clinicians..."
                  noEntriesFoundLabel="No Clinicians Found"
                >
                </ngx-mat-select-search>
              </mat-option>
              @for (
                provider of filteredProviders$ | async;
                track provider.npi
              ) {
                <mat-option [value]="provider.npi">
                  {{ provider.lastName }}, {{ provider.firstName }} <br /><span
                    class="npi-subtext"
                    >(NPI: {{ provider.npi }})</span
                  >
                </mat-option>
              }
            </mat-select>
            @if (isFieldInvalid("npi")) {
              <mat-error>Please select a clinician</mat-error>
            }
          </mat-form-field>

          <button
            mat-raised-button
            color="primary"
            type="submit"
            [disabled]="isLoading() || !isNpiValid() || hasNoTins"
            class="check-button"
          >
            @if (isLoading()) {
              <mat-spinner diameter="20"></mat-spinner>
              <span>Checking...</span>
            } @else {
              Check Eligibility
            }
          </button>
        </div>

        @if (hasEligibilityResult()) {
          <button
            mat-button
            type="button"
            (click)="clearResults()"
            class="clear-button"
          >
            <mat-icon>refresh</mat-icon>
            Clear & Check Different NPI
          </button>
        }
      </form>

      @if (isSelectedYearClosed) {
        <div class="error-banner">
          <mat-icon>lock</mat-icon>
          <span>The {{ selectedClosedYear }} submission window has closed. New submissions cannot be created. Please select {{ currentPerformanceYear }}.</span>
        </div>
      }

      @if (clinicianName()) {
        <div class="clinician-info">
          <div class="clinician-details">
            <span class="clinician-name">{{ clinicianName() }}</span>
            <span class="clinician-npi"
              >NPI: {{ eligibilityForm.get("npi")?.value }}</span
            >
          </div>
        </div>
      }
    </div>

    <div class="results-loading-wrapper">
      @if (isLoading()) {
        <div class="results-loading-overlay">
          <mat-spinner diameter="40"></mat-spinner>
          <span>Checking eligibility...</span>
        </div>
      }

    @if (needsOrgSelection()) {
      <div class="org-selection-section">
        <h3 class="section-title">Select Organization</h3>
        <p class="org-selection-subtitle">
          This clinician is associated with multiple organizations. Please
          select one:
        </p>

        <div class="org-selection-notice">
          <mat-icon>info</mat-icon>
          <div>
            <strong>Important:</strong> Make sure the
            <strong>Practice</strong> selected above matches the organization
            you choose below. If they don't match, please update the Practice
            dropdown before proceeding.
          </div>
        </div>

        <div class="org-cards">
          @for (
            org of eligibilityResult()?.mipsEligibilityOptions;
            track $index
          ) {
            <mat-card
              class="org-card"
              [class.selected]="selectedOrgIndex() === $index"
              (click)="selectOrganization(org, $index)"
            >
              <mat-card-content>
                <h4>{{ org.organizationName }}</h4>
                <div class="org-eligibility-summary">
                  <div class="eligibility-item">
                    <mat-icon
                      [class.eligible]="org.individualEligible"
                      [class.opt-in-eligible]="
                        !org.individualEligible && org.individualOptInEligible
                      "
                      [class.ineligible]="
                        !org.individualEligible && !org.individualOptInEligible
                      "
                    >
                      {{
                        org.individualEligible
                          ? "check_circle"
                          : org.individualOptInEligible
                            ? "verified"
                            : "cancel"
                      }}
                    </mat-icon>
                    <div class="eligibility-label">
                      <span class="level-name">Individual</span>
                      @if (org.individualEligible) {
                        <span class="status-badge eligible">Eligible</span>
                      } @else if (org.individualOptInEligible) {
                        <span class="status-badge opt-in">Opt-In</span>
                      }
                    </div>
                  </div>
                  <div class="eligibility-item">
                    <mat-icon
                      [class.eligible]="org.groupEligible"
                      [class.opt-in-eligible]="
                        !org.groupEligible && org.groupOptInEligible
                      "
                      [class.ineligible]="
                        !org.groupEligible && !org.groupOptInEligible
                      "
                    >
                      {{
                        org.groupEligible
                          ? "check_circle"
                          : org.groupOptInEligible
                            ? "verified"
                            : "cancel"
                      }}
                    </mat-icon>
                    <div class="eligibility-label">
                      <span class="level-name">Group</span>
                      @if (org.groupEligible) {
                        <span class="status-badge eligible">Eligible</span>
                      } @else if (org.groupOptInEligible) {
                        <span class="status-badge opt-in">Opt-In</span>
                      }
                    </div>
                  </div>
                  <div class="eligibility-item">
                    <mat-icon
                      [class.eligible]="org.subgroupEligible"
                      [class.opt-in-eligible]="
                        !org.subgroupEligible && org.subgroupOptInEligible
                      "
                      [class.ineligible]="
                        !org.subgroupEligible && !org.subgroupOptInEligible
                      "
                    >
                      {{
                        org.subgroupEligible
                          ? "check_circle"
                          : org.subgroupOptInEligible
                            ? "verified"
                            : "cancel"
                      }}
                    </mat-icon>
                    <div class="eligibility-label">
                      <span class="level-name">Subgroup</span>
                      @if (org.subgroupEligible) {
                        <span class="status-badge eligible">Eligible</span>
                      } @else if (org.subgroupOptInEligible) {
                        <span class="status-badge opt-in">Opt-In</span>
                      }
                    </div>
                  </div>
                </div>
              </mat-card-content>
            </mat-card>
          }
        </div>
      </div>
    }

    @if (hasEligibilityResult() && !needsOrgSelection()) {
      <div class="results-section">
        <div class="eligibility-status">
          <h3 class="section-title">Eligibility Status</h3>
          <div class="status-grid">
            <div class="status-item">
              <mat-icon
                [class.eligible]="isIndividualEligible()"
                [class.opt-in-eligible]="
                  !isIndividualEligible() && isIndividualOptInEligible()
                "
                [class.ineligible]="
                  !isIndividualEligible() && !isIndividualOptInEligible()
                "
              >
                {{
                  isIndividualEligible()
                    ? "check_circle"
                    : isIndividualOptInEligible()
                      ? "verified"
                      : "cancel"
                }}
              </mat-icon>
              <span>
                Individual:
                {{
                  isIndividualEligible()
                    ? "Eligible"
                    : isIndividualOptInEligible()
                      ? "Opt-In Available"
                      : "Not Eligible"
                }}
              </span>
            </div>
            <div class="status-item">
              <mat-icon
                [class.eligible]="isGroupEligible()"
                [class.opt-in-eligible]="
                  !isGroupEligible() && isGroupOptInEligible()
                "
                [class.ineligible]="
                  !isGroupEligible() && !isGroupOptInEligible()
                "
              >
                {{
                  isGroupEligible()
                    ? "check_circle"
                    : isGroupOptInEligible()
                      ? "verified"
                      : "cancel"
                }}
              </mat-icon>
              <span>
                Group:
                {{
                  isGroupEligible()
                    ? "Eligible"
                    : isGroupOptInEligible()
                      ? "Opt-In Available"
                      : "Not Eligible"
                }}
              </span>
            </div>
            <div class="status-item">
              <mat-icon
                [class.eligible]="mvpRegistered()"
                [class.ineligible]="!mvpRegistered()"
              >
                {{ mvpRegistered() ? "check_circle" : "cancel" }}
              </mat-icon>
              <span
                >MVP Registration:
                {{ mvpRegistered() ? "Registered" : "Not Registered" }}</span
              >
            </div>
          </div>

          @if (showMvpIdRefreshAction) {
            <div class="mvp-id-refresh-notice">
              <mat-icon>info</mat-icon>
              <div>
                No MVP Registration on file. Please recheck registration.
              </div>
              <button
                mat-stroked-button
                type="button"
                (click)="refreshMvpRegistration()"
              >
                Re-check MVP Registration
              </button>
            </div>
          }

          @if (hasAnyOptInEligibility() && !hasAnyEligibility()) {
            <div class="opt-in-notice">
              <mat-icon>info</mat-icon>
              <div>
                <strong>Voluntary Participation:</strong> You are not required
                to participate in MIPS, but you are eligible to voluntarily
                opt-in for reporting.
              </div>
            </div>
          }
        </div>

        <div class="reporting-type-section" [class.closed]="isSelectedYearClosed">
          <h3 class="section-title">Select Reporting Option</h3>

          <div class="reporting-type-cards">
            @for (type of reportingTypes; track type.value) {
              <div
                class="reporting-type-card"
                [class.selected]="
                  eligibilityForm.get('reportingType')?.value === type.value
                "
                [class.mvp-warning]="
                  (type.value === 'mvp' || type.value === 'both') &&
                  !mvpRegistered()
                "
                (click)="onReportingTypeSelected(type.value)"
                [attr.role]="'button'"
                [attr.tabindex]="0"
              >
                <h4>{{ type.label }}</h4>
                @if (type.hint) {
                  <div
                    class="card-hint"
                    [class.warning-hint]="
                      (type.value === 'mvp' || type.value === 'both') &&
                      !mvpRegistered()
                    "
                  >
                    @if (
                      (type.value === "mvp" || type.value === "both") &&
                      !mvpRegistered()
                    ) {
                      <mat-icon>warning</mat-icon>
                    }
                    {{ type.hint }}
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>
    }

    @if (hasReportingTypeSelected()) {
      <div class="reporting-level-section" [class.closed]="isSelectedYearClosed">
        <h3 class="section-title">Select Reporting Level</h3>

        @if (eligibilityForm.get("reportingType")?.value === "both") {
          <div class="both-mode-notice">
            <mat-icon>info</mat-icon>
            <div>
              <strong>Important:</strong> When selecting "Both", the same
              reporting level will be used for both Traditional MIPS and MVP
              submissions. Please select <strong>Individual</strong> or
              <strong>Group</strong> below. <br /><br />
              <em
                >Note: Subgroup is only available for MVP-only submissions, not
                when creating both types together.</em
              >
            </div>
          </div>
        }

        <div class="reporting-options">
          @if (shouldShowReportingLevel("individual")) {
            <mat-card
              class="reporting-card"
              [class.enabled]="hasEligibilityResult()"
              [class.selected]="isReportingLevelSelected('individual')"
              [class.not-eligible-warning]="
                hasEligibilityResult() &&
                !isIndividualEligible() &&
                !isIndividualOptInEligible()
              "
              (click)="selectReportingLevel('individual')"
            >
              <mat-card-content>
                <div class="card-icon">
                  <mat-icon class="individual-person-icon">person</mat-icon>
                </div>

                <h4 class="card-title">Individual Reporting</h4>

                @if (hasEligibilityResult()) {
                  @if (isIndividualEligible()) {
                    <div class="eligible-banner eligible">
                      <mat-icon>check_circle</mat-icon>
                      <span>Eligible</span>
                    </div>
                  } @else if (isIndividualOptInEligible()) {
                    <div class="eligible-banner opt-in">
                      <mat-icon>verified</mat-icon>
                      <span>Opt-In Available</span>
                    </div>
                  } @else {
                    <div class="warning-banner">
                      <mat-icon>warning</mat-icon>
                      <span>Not Eligible</span>
                    </div>
                  }
                }

                @if (eligibilityForm.get("reportingType")?.value === "both") {
                  <p class="card-note applies-to-both">
                    <mat-icon>info</mat-icon>
                    This will apply to both Traditional and MVP
                  </p>
                }

                <p class="card-description">
                  An individual is defined as a single clinician identified by
                  their:
                </p>

                <ul class="card-list">
                  <li>Individual National Provider Identifier (NPI)</li>
                  <li>Tied to a single Taxpayer Identification Number (TIN)</li>
                </ul>

                <p class="card-subtitle">If you report as an individual:</p>

                <ul class="card-list">
                  <li>
                    You will report measure and activities for the
                    practice(s)/TIN(s) under which you are MIPS - eligible
                    clinician and will be assessed across all 4 MIPS performance
                    categories at the individual level.
                  </li>
                  <li>
                    Your payment adjustment will be based on your Final Score
                    derived from the 4 MIPS performance categories.
                  </li>
                </ul>
              </mat-card-content>
            </mat-card>
          }

          @if (shouldShowReportingLevel("group")) {
            <mat-card
              class="reporting-card"
              [class.enabled]="hasEligibilityResult() && !isGroupLevelBlocked"
              [class.selected]="isReportingLevelSelected('group')"
              [class.not-eligible-warning]="
                hasEligibilityResult() &&
                !isGroupEligible() &&
                !isGroupOptInEligible()
              "
              [class.disabled]="isGroupLevelBlocked"
              (click)="!isGroupLevelBlocked && selectReportingLevel('group')"
            >
              <mat-card-content>
                <div class="card-icon">
                  <mat-icon class="group-person-icon">person</mat-icon>
                  <mat-icon class="group-person-icon">person</mat-icon>
                  <mat-icon class="group-person-icon">person</mat-icon>
                </div>

                <h4 class="card-title">Group Reporting</h4>

                @if (isGroupLevelBlocked) {
                  <div class="warning-banner">
                    <mat-icon>lock</mat-icon>
                    <span>{{ groupLevelBlockedMessage }}</span>
                  </div>
                }

                @if (hasEligibilityResult()) {
                  @if (isGroupEligible()) {
                    <div class="eligible-banner eligible">
                      <mat-icon>check_circle</mat-icon>
                      <span>Eligible</span>
                    </div>
                  } @else if (isGroupOptInEligible()) {
                    <div class="eligible-banner opt-in">
                      <mat-icon>verified</mat-icon>
                      <span>Opt-In Available</span>
                    </div>
                  } @else {
                    <div class="warning-banner">
                      <mat-icon>warning</mat-icon>
                      <span>Not Eligible</span>
                    </div>
                  }
                }

                @if (eligibilityForm.get("reportingType")?.value === "both") {
                  <p class="card-note applies-to-both">
                    <mat-icon>info</mat-icon>
                    This will apply to both Traditional and MVP
                  </p>
                }

                <p class="card-description">
                  A group is defined as a single TIN with 2 or more clinicians
                  (of which at least one clinician within the group must be MIPS
                  eligible) as identified by their NPI, who have reassigned
                  their Medicare billing rights to a single TIN.
                </p>

                <p class="card-subtitle">If you report as a group:</p>

                <ul class="card-list">
                  <li>
                    You must meet the definition of a group at all times during
                    the performance period and aggregate the group's performance
                    data across the 4 MIPS performance categories for a single
                    TIN.
                  </li>
                  <li>
                    Each MIPS-eligible clinician in the group will receive the
                    same payment adjustment based on the group's performance
                    across all 4 MIPS performance categories.
                  </li>
                </ul>
              </mat-card-content>
            </mat-card>
          }

          @if (shouldShowReportingLevel("subgroup")) {
            <mat-card
              class="reporting-card"
              [class.enabled]="hasEligibilityResult() && !isSubgroupLevelBlocked"
              [class.selected]="isReportingLevelSelected('subgroup')"
              [class.disabled]="isSubgroupLevelBlocked"
              [class.mvp-not-registered-warning]="!mvpRegistered()"
              [class.not-eligible-warning]="
                hasEligibilityResult() &&
                !isSubgroupEligible() &&
                !isSubgroupOptInEligible()
              "
              (click)="!isSubgroupLevelBlocked && selectReportingLevel('subgroup')"
            >
              <mat-card-content>
                <div class="card-icon subgroup-icon-container">
                  <div class="subgroup-main">
                    <mat-icon class="subgroup-person-large">person</mat-icon>
                    <mat-icon class="subgroup-person-large">person</mat-icon>
                    <mat-icon class="subgroup-person-large">person</mat-icon>
                  </div>
                  <div class="subgroup-bottom">
                    <div class="subgroup-small">
                      <mat-icon class="subgroup-person-small">person</mat-icon>
                      <mat-icon class="subgroup-person-small">person</mat-icon>
                      <mat-icon class="subgroup-person-small">person</mat-icon>
                    </div>
                    <div class="subgroup-small">
                      <mat-icon class="subgroup-person-small">person</mat-icon>
                      <mat-icon class="subgroup-person-small">person</mat-icon>
                      <mat-icon class="subgroup-person-small">person</mat-icon>
                    </div>
                  </div>
                </div>

                <h4 class="card-title">Subgroup Reporting</h4>

                @if (isSubgroupLevelBlocked) {
                  <div class="warning-banner">
                    <mat-icon>lock</mat-icon>
                    <span>{{ subgroupLevelBlockedMessage }}</span>
                  </div>
                }

                @if (!mvpRegistered()) {
                  <div class="warning-banner mvp-warning">
                    <mat-icon>warning</mat-icon>
                    <span>Not MVP Registered</span>
                  </div>
                }

                @if (hasEligibilityResult()) {
                  @if (isSubgroupEligible()) {
                    <div class="eligible-banner eligible">
                      <mat-icon>check_circle</mat-icon>
                      <span>Eligible</span>
                    </div>
                  } @else if (isSubgroupOptInEligible()) {
                    <div class="eligible-banner opt-in">
                      <mat-icon>verified</mat-icon>
                      <span>Opt-In Available</span>
                    </div>
                  } @else {
                    <div class="warning-banner">
                      <mat-icon>warning</mat-icon>
                      <span>Not Eligible</span>
                    </div>
                  }
                }

                <p class="card-note">
                  The subgroup participation option is only available when
                  reporting MIPS Value Pathways (MVPs).
                </p>

                <p class="card-description">
                  A subgroup is a subset of a group which contains at least one
                  MIPS eligible clinician and is identified by a combination of
                  the group's subgroup identifier, and each eligible clinician's
                  NPI.
                </p>

                <p class="card-subtitle">If you report as a subgroup:</p>

                <ul class="card-list">
                  <li>
                    You must meet the definition of a subgroup at all times
                    during the performance period and aggregate the subgroup's
                    performance data across the 4 MIPS performance categories.
                  </li>
                  <li>
                    Each MIPS-eligible clinician in the subgroup will receive
                    the same payment adjustment based on the subgroup's
                    performance across all 4 MIPS performance categories.
                  </li>
                </ul>
              </mat-card-content>
            </mat-card>
          }
        </div>
      </div>
    }
    </div>
    }

  </mat-card-content>
</mat-card>
